/**
 * RunClassificationPanel — NC-1288 (Soll 1, retro path).
 *
 * Lets the tutor state "Produktiv" or "Test" on an EXISTING run. `RunCreateDialog`
 * covers the label at creation time; this is the only way to change it afterwards,
 * and that is the common case: a run is usually only recognised as a test run once
 * it is already up, and every run created before NC-1288 has no label at all.
 *
 * Why this sits next to `RunFastCleanupPanel` rather than somewhere else: the two
 * options are easy to confuse — both say "Testlauf" in everyday speech — and the
 * issue is explicit that they stay separate concerns. Side by side, each card can
 * name what the other one is NOT. `fastCleanup` answers "wie lange bleibt der Lauf
 * liegen?", this one answers "zählt der Lauf als echte Schulung?".
 *
 * One limitation worth stating, because it looks like a bug otherwise: the read
 * paths (`runs-list`, `runs-details`) normalise `classification` via
 * api/shared/runClassification.ts, so the frontend receives the EFFECTIVE value
 * and cannot tell a persisted marker from the legacy `E2E-` RunId derivation.
 * Selecting the value that is already shown therefore sends nothing — there is
 * no client-side way to know it would turn an inferred label into a stated one.
 * The API's `noop` flag is still honoured, because a second operator editing the
 * same run concurrently makes that response reachable.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { runsApi } from '../services/api';
import { RunClassification } from '../services/types';

interface RunClassificationPanelProps {
  runId: string;
  /**
   * Effective classification as delivered by the API. Absent only if a caller
   * passes a raw doc — treated as 'produktiv', matching the API default.
   */
  classification?: RunClassification;
  /** operator+ — viewers see the state read-only. */
  canOperate: boolean;
  /** Re-fetch the run so the parent's copy matches what was just persisted. */
  onChanged?: () => void;
}

const LABEL: Record<RunClassification, string> = {
  produktiv: 'Produktiv',
  test: 'Test',
};

const EXPLANATION: Record<RunClassification, string> = {
  produktiv: 'Zählt als echte Schulung und geht in die Auswertung ein.',
  test: 'Zählt als Test-/E2E-Lauf und wird in der Auswertung nicht als echte Schulung geführt.',
};

export function RunClassificationPanel({
  runId,
  classification,
  canOperate,
  onChanged,
}: RunClassificationPanelProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    classification: RunClassification;
    unchanged: boolean;
  } | null>(null);

  const current: RunClassification = classification === 'test' ? 'test' : 'produktiv';

  const handleChange = async (next: RunClassification | null) => {
    // Real MUI hands us `null` when the already-selected button is clicked; the
    // test stub hands back the same value. Both mean "no change requested".
    if (next === null || next === current) return;
    // Guard the handler, not just the control: `disabled` is a rendering hint,
    // and this write changes numbers that get reported.
    if (!canOperate || saving) return;

    setSaving(true);
    setError(null);
    setResult(null);
    try {
      const response = await runsApi.setClassification(runId, next);
      setResult({
        classification: response.classification,
        unchanged: response.noop === true,
      });
      onChanged?.();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Kennzeichnung konnte nicht geändert werden.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Art des Laufs
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={current}
            onChange={(_e: unknown, next: RunClassification | null) => handleChange(next)}
            aria-label="Art des Laufs"
          >
            <ToggleButton
              value="produktiv"
              disabled={!canOperate || saving}
              aria-label="Als produktiven Lauf kennzeichnen"
            >
              {LABEL.produktiv}
            </ToggleButton>
            <ToggleButton
              value="test"
              disabled={!canOperate || saving}
              aria-label="Als Testlauf kennzeichnen"
            >
              {LABEL.test}
            </ToggleButton>
          </ToggleButtonGroup>
          {saving && <CircularProgress size={18} />}
        </Box>

        <Typography variant="body2" color="text.secondary">
          {EXPLANATION[current]}
        </Typography>

        {/* The distinction that the two adjacent cards exist to keep apart. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Betrifft nur die Auswertung — nicht das Aufräumen. Wie lange der Lauf liegen bleibt,
          steuert die Aufräum-Option.
        </Typography>

        {result && (
          <Alert
            severity={result.unchanged ? 'info' : 'success'}
            sx={{ mt: 2 }}
            onClose={() => setResult(null)}
          >
            {result.unchanged
              ? `Der Lauf war bereits als "${LABEL[result.classification]}" gekennzeichnet — keine Änderung.`
              : `Als "${LABEL[result.classification]}" gekennzeichnet. ${EXPLANATION[result.classification]}`}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default RunClassificationPanel;
